import { useState, useEffect } from "react"
import { Button, DatePicker, Flex, Input, Modal, Select, TimePicker } from "antd"
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { Text } from "ui/Typography"
import CustomDrawer from "ui/Drawer"
import { useDevice } from "hooks/useDevice"
import styles from "./AddOverrideModal.module.css"

const { RangePicker } = DatePicker

const INTERVAL_OPTIONS = [
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 60, label: "1 hour" },
  { value: 120, label: "2 hours" },
]

const generateTimes = (from, to, intervalMinutes) => {
  if (!from || !to || !intervalMinutes) return []
  const times = []
  let current = from.clone()
  while (current.isBefore(to) || current.isSame(to, "minute")) {
    times.push(current.format("hh:mm A"))
    current = current.add(intervalMinutes, "minute")
  }
  return times
}

const emptyDateRange = () => ({ id: Date.now(), value: null })

export const AddOverrideModal = ({ open, onClose, onSave, editingOverride = null }) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()

  const [name, setName] = useState("")
  const [dateRanges, setDateRanges] = useState([emptyDateRange()])
  const [fromTime, setFromTime] = useState(null)
  const [toTime, setToTime] = useState(null)
  const [interval, setInterval] = useState(null)
  const [previewTimes, setPreviewTimes] = useState([])

  useEffect(() => {
    if (!open) return
    if (editingOverride) {
      setName(editingOverride.name || "")
      setDateRanges(
        editingOverride.date_ranges?.length
          ? editingOverride.date_ranges.map((r, i) => ({
              id: i,
              value: r.start && r.end ? [dayjs(r.start), dayjs(r.end)] : null,
            }))
          : [emptyDateRange()]
      )
      setFromTime(editingOverride.from_time ? dayjs(editingOverride.from_time, "HH:mm") : null)
      setToTime(editingOverride.to_time ? dayjs(editingOverride.to_time, "HH:mm") : null)
      setInterval(editingOverride.interval || null)
      setPreviewTimes(editingOverride.start_times || [])
    } else {
      setName("")
      setDateRanges([emptyDateRange()])
      setFromTime(null)
      setToTime(null)
      setInterval(null)
      setPreviewTimes([])
    }
  }, [open, editingOverride])

  const handleAddRange = () => setDateRanges((prev) => [...prev, emptyDateRange()])

  const handleRemoveRange = (id) => setDateRanges((prev) => prev.filter((r) => r.id !== id))

  const handleRangeChange = (id, value) =>
    setDateRanges((prev) => prev.map((r) => (r.id === id ? { ...r, value } : r)))

  const handleGenerate = () => {
    setPreviewTimes(generateTimes(fromTime, toTime, interval))
  }

  const timeError = !!(fromTime && toTime && !toTime.isAfter(fromTime))
  const canGenerate = fromTime && toTime && interval && !timeError
  const canSave = name.trim() && previewTimes.length > 0

  const handleSave = () => {
    onSave({
      id: editingOverride?.id ?? Date.now(),
      name: name.trim(),
      date_ranges: dateRanges
        .filter((r) => r.value?.[0] && r.value?.[1])
        .map((r) => ({
          start: r.value[0].format("YYYY-MM-DD"),
          end: r.value[1].format("YYYY-MM-DD"),
        })),
      from_time: fromTime?.format("HH:mm") ?? null,
      to_time: toTime?.format("HH:mm") ?? null,
      interval,
      start_times: previewTimes,
    })
    onClose()
  }

  const modalTitle = t("listings.tabs.scheduling.sections.availability_schedule.override_modal.title")

  const content = (
    <>
      <Flex gap={24} className={styles.body}>
        {/* Left — Details */}
        <div className={styles.leftPanel}>
          <Text fw={600} className="block mb-l">
            {t("listings.tabs.scheduling.sections.availability_schedule.override_modal.details")}
          </Text>

          {/* Name */}
          <div className="mb-l">
            <Text className="block mb-xs">
              {t("listings.tabs.scheduling.sections.availability_schedule.override_modal.name")}
              <span className={styles.required}>*</span>
            </Text>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Holidays"
            />
          </div>

          {/* Date ranges */}
          <div className="mb-l">
            <Text className="block mb-s">
              {t("listings.tabs.scheduling.sections.availability_schedule.override_modal.dates")}
            </Text>
            <Flex vertical gap={8}>
              {dateRanges.map((range) => (
                <Flex key={range.id} align="center" gap={8}>
                  <RangePicker
                    value={range.value}
                    onChange={(val) => handleRangeChange(range.id, val)}
                    className={styles.rangePicker}
                    format="MM/DD/YYYY"
                  />
                  {dateRanges.length > 1 && (
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveRange(range.id)}
                      className={styles.deleteBtn}
                    />
                  )}
                </Flex>
              ))}
            </Flex>
            <Button type="link" className="pl-0 mt-s" onClick={handleAddRange}>
              {t("listings.tabs.scheduling.sections.availability_schedule.override_modal.add_more")}
            </Button>
          </div>

          <div className={styles.divider} />

          {/* Set Start Times */}
          <Text fw={600} className="block mb-l">
            {t("listings.tabs.scheduling.sections.availability_schedule.override_modal.set_start_times")}
          </Text>

          <Flex gap={16} className="mb-l">
            <div className={styles.timeField}>
              <Text className="block mb-xs">
                {t("listings.tabs.scheduling.sections.availability_schedule.override_modal.from")}
                <span className={styles.required}>*</span>
              </Text>
              <TimePicker
                needConfirm={false}
                use12Hours
                format="hh:mm A"
                value={fromTime}
                onChange={setFromTime}
                className={styles.timePicker}
              />
            </div>
            <div className={styles.timeField}>
              <Text className="block mb-xs">
                {t("listings.tabs.scheduling.sections.availability_schedule.override_modal.to")}
                <span className={styles.required}>*</span>
              </Text>
              <TimePicker
                needConfirm={false}
                use12Hours
                format="hh:mm A"
                value={toTime}
                onChange={setToTime}
                className={styles.timePicker}
                status={timeError ? "error" : ""}
              />
              {timeError && (
                <Text type="danger" size="sm" className="block mt-xxs">
                  {t("listings.tabs.scheduling.sections.availability_schedule.override_modal.to_after_from")}
                </Text>
              )}
            </div>
          </Flex>

          <div className="mb-l">
            <Text className="block mb-xs">
              {t("listings.tabs.scheduling.sections.availability_schedule.override_modal.interval")}
            </Text>
            <Select
              options={INTERVAL_OPTIONS}
              value={interval}
              onChange={setInterval}
              placeholder="Select"
              className={styles.intervalSelect}
            />
          </div>

          <Button
            type="primary"
            shape="round"
            disabled={!canGenerate}
            onClick={handleGenerate}
            icon={<PlusOutlined />}
          >
            {t("listings.tabs.scheduling.sections.availability_schedule.override_modal.generate")}
          </Button>
        </div>

        {/* Right — Preview */}
        <div className={styles.rightPanel}>
          <Text fw={600} className="block mb-l">
            {t("listings.tabs.scheduling.sections.availability_schedule.modal.preview_title")}
          </Text>
          {previewTimes.length === 0 ? (
            <Text type="secondary" className={styles.noPreview}>
              {t("listings.tabs.scheduling.sections.availability_schedule.modal.no_preview")}
            </Text>
          ) : (
            <Flex gap={8} wrap="wrap">
              {previewTimes.map((time) => (
                <span key={time} className={styles.timeTag}>{time}</span>
              ))}
            </Flex>
          )}
        </div>
      </Flex>

      {/* Footer */}
      <Flex justify="flex-end" gap={12} className={styles.footer}>
        <Button onClick={onClose}>{"Cancel"}</Button>
        <Button type="primary" disabled={!canSave} onClick={handleSave}>
          {t("listings.tabs.scheduling.sections.availability_schedule.override_modal.save")}
        </Button>
      </Flex>
    </>
  )

  if (isMobile) {
    return (
      <CustomDrawer open={open} setOpen={() => onClose()} title={modalTitle}>
        {content}
      </CustomDrawer>
    )
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={modalTitle}
      width={860}
      footer={null}
      destroyOnClose
    >
      {content}
    </Modal>
  )
}

AddOverrideModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  editingOverride: PropTypes.object,
}
